import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as echarts from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import * as ecStatRaw from 'echarts-stat';
import { buildConfigurableTooltipFormatter, formatTooltipValue, getTooltipLabel, buildConfigurableAxisLabel } from '../../utils/chart.utils';

const ecStat: any = ecStatRaw;

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    CapitalCasePipe,
  ],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrl: './quad-bubble-chart.component.scss'
})
export class QuadBubbleChartComponent implements AfterViewInit, OnDestroy {
  get resolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }
  @Input() data!: any;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  protected chart!: echarts.ECharts;

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (chartDom) {
      this.chart = echarts.init(chartDom, 'dvm');
      this.renderChart();
      window.addEventListener('resize', this.resizeChart);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.chart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chart) return;
      const dom = document.getElementById(this.data.widgetId);
      if (!dom) return;
      this.chart.dispose();
      this.chart = echarts.init(dom, 'dvm');
      this.renderChart();
    });
  }

  protected renderChart(): void {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    echarts.registerTransform(ecStat.transform.clustering);

    // Responsive font scale — shrinks on tablets (0.85), scales up on TV/4K (> 1)
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.max(1, Math.round(px * fs));

    const raw = this.resolvePath(this.data, this.data.series);
    const clusterCount = this.data.clusterCount || 4;
    const symbolSize = this.data.symbolSize || 15;

    const points: number[][] = [
      ...(raw?.q1 ?? []),
      ...(raw?.q2 ?? []),
      ...(raw?.q3 ?? []),
      ...(raw?.q4 ?? [])
    ];

    const dimensionClusterIndex = 2;

    const COLOR_ALL = [
      '#37A2DA', '#e06343', '#37a354',
      '#b55dba', '#b5bd48', '#8378EA', '#96BFFF'
    ];

    const option: echarts.EChartsOption = {
      dataset: [
        { source: points },
        {
          transform: {
            type: 'ecStat:clustering',
            config: {
              clusterCount,
              outputType: 'single',
              outputClusterIndexDimension: dimensionClusterIndex
            }
          }
        }
      ],
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = params.data;
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const idx = parseInt(field.key, 10);
                const rawValue = !isNaN(idx) ? d[idx] : d[field.key];
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          return (params: any) => `x: ${params.data[0]}, y: ${params.data[1]}`;
        })()
      },
      visualMap: {
        type: 'piecewise',
        dimension: dimensionClusterIndex,
        selectedMode: 'multiple',
        orient: 'horizontal',
        left: 'center',
        top: sc(4),
        itemGap: sc(10),
        itemWidth: sc(14),
        itemHeight: sc(12),
        textStyle: { fontSize: sc(11) },
        pieces: Array.from({ length: clusterCount }, (_, i) => ({
          value: i,
          label: `Cluster ${i}`,
          color: COLOR_ALL[i]
        }))
      },
      xAxis: {
        min: this.data.xAxis?.min ?? 0,
        max: this.data.xAxis?.max ?? 10,
        splitLine: { show: false },
        axisLabel: { fontSize: sc(10), hideOverlap: true },
      },
      yAxis: {
        min: this.data.yAxis?.min ?? 0,
        max: this.data.yAxis?.max ?? 10,
        splitLine: { show: false },
        axisLabel: {
          fontSize: sc(10),
          hideOverlap: true,
          ...((buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) as any) || {}),
        },
      },
      grid: { left: '3%', right: '5%', top: '18%', bottom: '10%', containLabel: true },
      series: [
        {
          type: 'scatter',
          datasetIndex: 1,
          encode: { tooltip: [0, 1] },
          symbolSize,
          itemStyle: {
            borderColor: 'transparent'
          },
          markArea: {
            silent: true,
            itemStyle: { opacity: 0.2 },
            data: this.getQuadrantAreas()
          }
        }
      ]
    };

    this.chart.setOption(option);
  }

  protected getQuadrantAreas(): any[] {
    return (this.data.quadrantBackgrounds || []).map((quad: any) => ([
      {
        name: '',
        xAxis: quad.xAxis,
        yAxis: quad.yAxis,
        itemStyle: { color: quad.color }
      },
      {
        xAxis: quad.x2,
        yAxis: quad.y2
      }
    ]));
  }

  protected resolvePath(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  protected resizeChart = () => {
    this.chart?.resize();
  };

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeChart);
    this.chart?.dispose();
  }
}
